import {
  StudenStatusOptionsResponse,
  StudentStatusListResponse,
  StudentStatusRequest,
} from '../../types/studentStatusTypes';
import { STUDENT_STATUS } from '../endpoints';
import request from '../request';

/**
 * Recupera el listado de estado del alumno
 * mediante los filtros de estado y nombre/id
 * @param data
 * @returns
 */
export const requestStudentStatusList = (data: {
  items_per_page: number;
  page: number;
  status?: string | null;
  campusId?: string | null;
  search_text?: string | null;
}) => {
  return request<StudentStatusListResponse>(`${STUDENT_STATUS.LIST}`, {
    params: data,
  });
};

/**
 * Recupera todos los estados del alumno para ser utilizados
 * como filtro del listado
 * @returns
 */
export const requestStudentStatusOptions = () => {
  return request<StudenStatusOptionsResponse>(
    `${STUDENT_STATUS.STATUS_OPTIONS}`,
  );
};

/**
 * Subir documentación para validar cambio de estado de alumno
 */
export const uploadDocumentationChangeStatus = ({
  file,
  studyPlanEnrollmentId,
  newStatus,
  startDate,
  endDate,
  observations,
}: {
  file: File | null;
  studyPlanEnrollmentId: number;
  newStatus: string;
  startDate?: string;
  endDate?: string;
  observations?: string;
}) => {
  const formData = new FormData();
  if (file) {
    formData.append('doc', file);
  }
  formData.append('newStatus', newStatus);
  if (startDate) {
    formData.append('startDate', startDate);
  }
  if (endDate) {
    formData.append('endDate', endDate);
  }
  if (observations) {
    formData.append('observations', observations);
  }
  return request(STUDENT_STATUS.PUT_CHANGE_STATUS(studyPlanEnrollmentId), {
    method: 'put',
    data: formData,
  });
};

/**
 * Obtiene el estado de un cambio de estado de un alumno
 */
export const requestStudentStatus = ({
  studyPlanEnrollmentId,
}: {
  studyPlanEnrollmentId: number;
}) => {
  return request<StudentStatusRequest>(
    STUDENT_STATUS.GET_STUDENT_STATUS(studyPlanEnrollmentId),
  );
};

/**
 * Se actualiza la documentación para validar cambio de estado de alumno
 */
export const updateDocumentationChangeStatus = ({
  file,
  studyPlanEnrollmentId,
  startDate,
  endDate,
  observations,
}: {
  file?: File | null;
  studyPlanEnrollmentId: number;
  startDate?: string;
  endDate?: string;
  observations?: string;
}) => {
  const formData = new FormData();
  if (file) {
    formData.append('doc', file);
  }
  if (startDate) {
    formData.append('startDate', startDate);
  }
  if (endDate) {
    formData.append('endDate', endDate);
  }
  if (observations) {
    formData.append('observations', observations);
  }
  return request(
    STUDENT_STATUS.PUT_UPDATE_STATUS_DOCUMENT(studyPlanEnrollmentId),
    {
      method: 'put',
      data: formData,
    },
  );
};

/**
 * Obtiene reporte del listado de los alumnos
 * con su estado asociado
 */
export const getReportStatusStudent = (data: {
  status?: string | null;
  search_text?: string | null;
  campusId?: string | null;
}) => {
  return request(STUDENT_STATUS.REPORT_STATUS_STUDENT, {
    responseType: 'blob',
    params: data,
  });
};

/**
 * HU 15404 — Bulk inactivar por continuidad.
 * Tipos y requests para alimentar la modal de inactivación masiva.
 */
export type BulkInactivateContinuityFilters = {
  periodId: number;
  schoolIds?: number[];
  curriculumIds?: number[];
};

export type BulkInactivateContinuityPeriod = {
  id: number;
  name?: string;
  startDate?: string;
};

export type BulkInactivateContinuityCurriculum = {
  id: number;
  name?: string;
};

/**
 * Lista los periodos con eventos de continuidad V2,
 * ordenados por startDate DESC.
 */
export const requestBulkInactivateContinuityPeriods = () =>
  request<BulkInactivateContinuityPeriod[]>(
    STUDENT_STATUS.BULK_INACTIVATE_CONTINUITY_PERIODS,
  );

/**
 * Lista las mallas curriculares (id + name), opcionalmente filtradas
 * por escuelas. Drives the cascading Plan de Estudio multi-select.
 */
export const requestBulkInactivateContinuityCurriculums = (params: {
  schoolIds?: number[];
}) =>
  request<BulkInactivateContinuityCurriculum[]>(
    STUDENT_STATUS.BULK_INACTIVATE_CONTINUITY_CURRICULUMS,
    { params },
  );

/**
 * Calcula la cantidad de matrículas candidatas a inactivación
 * según los filtros (sin mutar estado).
 */
export const requestBulkInactivateContinuityPreview = (
  filters: BulkInactivateContinuityFilters,
) =>
  request<{ count: number }>(
    STUDENT_STATUS.BULK_INACTIVATE_CONTINUITY_PREVIEW,
    {
      method: 'post',
      data: filters,
    },
  );

/**
 * Ejecuta la inactivación masiva con los filtros indicados.
 */
export const requestBulkInactivateContinuityExecute = (
  filters: BulkInactivateContinuityFilters,
) =>
  request<{ affected: number }>(
    STUDENT_STATUS.BULK_INACTIVATE_CONTINUITY_EXECUTE,
    {
      method: 'post',
      data: filters,
    },
  );
